import { LogOut } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";
import { createPortal } from "react-dom";

const MENU_WIDTH = 220;

interface UserMenuProps {
  email: string;
  initials: string;
  logoutLabel: string;
  onLogout: () => void;
}

export function UserMenu({ email, initials, logoutLabel, onLogout }: UserMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = (): void => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node))
        return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={email}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className="grid h-[30px] w-[30px] place-items-center rounded-full text-xs font-bold text-white focus-ring"
        style={{ background: "linear-gradient(135deg,#6366f1,#0f62fe)" }}
      >
        {initials}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: pos.top, right: pos.right, width: MENU_WIDTH }}
            className="z-[80] rounded-xl border border-[var(--premium-border)] bg-white p-1.5 shadow-[var(--sh-pop)]"
          >
            <div
              className="truncate px-2.5 py-1.5 text-xs text-[var(--text-subtle)]"
              data-testid="topbar-user-email"
            >
              {email}
            </div>
            <div className="my-1 h-px bg-[var(--premium-border)]" />
            <button
              type="button"
              role="menuitem"
              data-testid="topbar-logout"
              onClick={() => {
                onLogout();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-[var(--text-primary)] transition hover:bg-[var(--canvas)]"
            >
              <LogOut className="h-4 w-4 text-[var(--text-subtle)]" aria-hidden />
              {logoutLabel}
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
